import { parseEntityRef, Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  useEntity,
  catalogApiRef,
  EntityTable,
} from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';
import Typography from '@material-ui/core/Typography';

const resourceColumns = [
  EntityTable.columns.createEntityRefColumn({ defaultKind: 'resource' }),
  EntityTable.columns.createOwnerColumn(),
  EntityTable.columns.createSpecTypeColumn(),
  EntityTable.columns.createMetadataDescriptionColumn(),
];

/**
 * Collects all unique entity refs from the entity's relations
 * where the target is kind=Resource, regardless of relation type.
 * This captures hasPart, heroku-hasAddon, heroku-hasSpace, etc.
 */
function getResourceRefs(entity: Entity): string[] {
  const seen = new Set<string>();
  const refs: string[] = [];

  for (const rel of entity.relations ?? []) {
    try {
      const parsed = parseEntityRef(rel.targetRef);
      if (parsed.kind.toLocaleLowerCase('en-US') === 'resource') {
        const normalized = rel.targetRef.toLocaleLowerCase('en-US');
        if (!seen.has(normalized)) {
          seen.add(normalized);
          refs.push(rel.targetRef);
        }
      }
    } catch {
      // skip unparseable refs
    }
  }

  return refs;
}

export function HerokuHasResourcesCard(props: { variant?: 'gridItem' }) {
  const { variant = 'gridItem' } = props;
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const {
    loading,
    value: entities,
    error,
  } = useAsync(async () => {
    const refs = getResourceRefs(entity);
    if (refs.length === 0) return [];

    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: refs,
    });
    return items.filter((e): e is Entity => Boolean(e));
  }, [entity]);

  const title = 'Has resources';

  if (loading) {
    return (
      <InfoCard variant={variant} title={title}>
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard variant={variant} title={title}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  return (
    <EntityTable
      title={title}
      variant={variant}
      emptyContent={
        <Typography variant="body1">
          No resources found for this entity.
        </Typography>
      }
      columns={resourceColumns}
      entities={entities ?? []}
    />
  );
}
